import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useLayoutEffect, useRef } from "react";
import {
  getIsNavigatingHistory,
  getKey,
  getPopstateTimestamp,
  getScrollFromState,
  getScrollTimestamp,
  setCurrentScrollHistory
} from "./storage";
import { isRecord } from "./util";
const getWindowScroll = () => [window.scrollX, window.scrollY];
const memoizationIntervalLimit = 601;
const safariBugWorkaroundTimeThreshold = 2e3;
const getState = () => {
  const state = window.history.state;
  return isRecord(state) ? state : null;
};
const restoreScrollFromState = (state) => {
  const scroll = getScrollFromState(state);
  if (scroll !== null) {
    const [x, y] = scroll;
    window.scrollTo({
      behavior: "instant",
      left: x,
      top: y
    });
  }
};
const scrollMemoIntervalCountLimit = 2;
const restoreCurrentScrollPosition = () => {
  restoreScrollFromState(getState());
};
const defaultMemoInterval = 0;
const numericTrue = 1;
const defaultX = 0;
const defaultY = 0;
const useScrollRestorer = () => {
  const pathname = usePathname();
  const searchparams = useSearchParams();
  useLayoutEffect(() => {
    restoreCurrentScrollPosition();
  }, [pathname, searchparams]);
  const scrollMemoTimeoutRef = useRef(void 0);
  const scrollMemoCountInInterval = useRef(defaultMemoInterval);
  const isSafariWorkaroundAllowedRef = useRef(false);
  useEffect(() => {
    window.history.scrollRestoration = "manual";
    const navigationListener = ({ state: eState }) => {
      cancelDelayedScrollMemoization();
      isSafariWorkaroundAllowedRef.current = true;
      const state = (isRecord(eState) ? eState : null) ?? {};
      window.history.replaceState({
        ...state,
        [getKey("is_navigating_history")]: numericTrue,
        [getKey("popstate_timestamp")]: (/* @__PURE__ */ new Date()).getTime()
      }, "");
    };
    const workaroundSafariBreaksScrollRestoration = ([x, y]) => {
      const state = getState();
      if (x === defaultX && y === defaultY && isSafariWorkaroundAllowedRef.current) {
        const isWorkaroundAllowed = (() => {
          const timeNavigated = getPopstateTimestamp(state);
          if (timeNavigated === null) {
            return false;
          }
          return (/* @__PURE__ */ new Date()).getTime() - timeNavigated < safariBugWorkaroundTimeThreshold;
        })();
        const isNavHistory = getIsNavigatingHistory(state);
        if (isWorkaroundAllowed && isNavHistory) {
          restoreCurrentScrollPosition();
          isSafariWorkaroundAllowedRef.current = false;
          return true;
        }
      }
      return false;
    };
    const rememberScrollPosition = (pos) => {
      cancelDelayedScrollMemoization();
      setCurrentScrollHistory(pos);
    };
    const unmountNavigationListener = () => {
      window.removeEventListener("popstate", navigationListener);
    };
    const mountNavigationListener = () => {
      window.addEventListener("popstate", navigationListener, {
        passive: true
      });
    };
    const cancelDelayedScrollMemoization = () => {
      if (scrollMemoTimeoutRef.current !== void 0) {
        clearTimeout(scrollMemoTimeoutRef.current);
        scrollMemoTimeoutRef.current = void 0;
      }
    };
    const scrollMemoizationHandler = (pos) => {
      const isScrollMemoAllowedNow = () => {
        const timestamp = getScrollTimestamp(getState());
        if (timestamp === null) {
          return true;
        }
        return (/* @__PURE__ */ new Date()).getTime() - timestamp > memoizationIntervalLimit;
      };
      const isAllowedNow = isScrollMemoAllowedNow();
      if (isAllowedNow) {
        scrollMemoCountInInterval.current = defaultMemoInterval;
      }
      if (isAllowedNow || scrollMemoCountInInterval.current < scrollMemoIntervalCountLimit) {
        scrollMemoCountInInterval.current++;
        rememberScrollPosition(pos);
      } else {
        if (scrollMemoTimeoutRef.current === void 0) {
          scrollMemoTimeoutRef.current = setTimeout(() => {
            rememberScrollPosition(pos);
            scrollMemoCountInInterval.current = defaultMemoInterval;
            scrollMemoTimeoutRef.current = void 0;
          }, memoizationIntervalLimit);
        }
      }
    };
    const scrollListener = () => {
      cancelDelayedScrollMemoization();
      const scroll = getWindowScroll();
      workaroundSafariBreaksScrollRestoration(scroll);
      scrollMemoizationHandler(scroll);
    };
    const mountScrollListener = () => {
      window.addEventListener("scroll", scrollListener, {
        passive: true
      });
    };
    const unmountScrollListener = () => {
      window.removeEventListener("scroll", scrollListener);
    };
    mountNavigationListener();
    mountScrollListener();
    return () => {
      unmountNavigationListener();
      unmountScrollListener();
      cancelDelayedScrollMemoization();
    };
  }, []);
};
var useScrollRestorer_default = useScrollRestorer;
export {
  useScrollRestorer_default as default
};
//# sourceMappingURL=useScrollRestorer.js.map